/**
 * Data "de verdade" de um registro clínico (ClinicalRecord) — a data do
 * ATENDIMENTO, não a do banco.
 *
 * Por que isto existe: na migração do HiDoctor, todo registro foi gravado com
 * `createdAt = serverTimestamp()` (o instante em que a migração rodou,
 * jun/2026). A data ORIGINAL do atendimento foi preservada à parte, em
 * `recordDate` (e, como fallback, em `content.date`). Quem exibe/ordena por
 * `createdAt` mostra todos os ~40k registros legados na data da migração — foi
 * exatamente o bug relatado no prontuário. Use `recordEventDate` em qualquer
 * lugar que precise da data do atendimento (timeline, laudos, resumos).
 */

/**
 * Converte um valor de data do Firestore — epoch ms (`number`), `Timestamp`
 * (`{ seconds }` ou com `.toMillis()`) ou indefinido — em epoch ms. Retorna 0
 * quando ausente/ inválido.
 */
export function tsToMs(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value && typeof value === 'object') {
    const o = value as { seconds?: number; toMillis?: () => number };
    if (typeof o.toMillis === 'function') {
      const ms = o.toMillis();
      return Number.isFinite(ms) ? ms : 0;
    }
    if (typeof o.seconds === 'number') return o.seconds * 1000;
  }
  return 0;
}

/**
 * Reprojeta os COMPONENTES UTC de um timestamp para um timestamp que, lido em
 * horário LOCAL, exibe exatamente esses componentes — anulando o fuso.
 *
 * As datas legadas do HiDoctor foram gravadas na migração via
 * `Date.UTC(componentes da data original)`. Como o HiDoctor exporta só a data
 * (99,4% dos registros caem em 00:00:00 UTC), exibi-las direto em horário de
 * Brasília (UTC-3) jogaria o registro pro DIA ANTERIOR. Mesmo princípio do fix
 * dos agendamentos (migration/fix-appointment-dates.mjs).
 */
function utcComponentsToLocal(ms: number): number {
  const d = new Date(ms);
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
  ).getTime();
}

/** Campos de data que um registro pode carregar (estrutural — não exige o
 *  tipo completo `ClinicalRecord`, então serve pros vários call sites). */
export interface RecordDateFields {
  recordDate?: unknown;
  content?: unknown;
  createdAt?: unknown;
}

/**
 * Epoch ms da data do atendimento que o registro representa.
 *
 * - Registros MIGRADOS do HiDoctor: data original do atendimento, vinda de
 *   `recordDate` (ou, como fallback, `content.date`), reprojetada de UTC p/
 *   local para não escorregar de dia (ver `utcComponentsToLocal`).
 * - Registros criados no dratento: não têm `recordDate`/`content.date`, então
 *   caem no `createdAt`, que aí é o instante real de criação (sem reprojeção).
 */
export function recordEventDate(record: RecordDateFields): number {
  const legacy =
    tsToMs(record.recordDate) ||
    tsToMs((record.content as { date?: unknown } | null | undefined)?.date);
  if (legacy) return utcComponentsToLocal(legacy);
  return tsToMs(record.createdAt);
}
